import React, { useCallback, useState, cloneElement } from 'react';
import pick from 'lodash/pick';
import identity from 'lodash/identity';
import moment from 'moment';
import { Input as InputUi } from 'reactstrap';
import Datetime from 'react-datetime';
import { createFormField } from 'Shared/utils/createFormField';

import 'react-datetime/css/react-datetime.css';
import './index.scss';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpBackdrop from 'Wpds/components/backdrop/WpBackdrop';

type Props = {
  touched: boolean;
  error: string;
  dateFormat: string;
  onChange: Function;
  value: string;
  className: string;
  icon: React.ReactNode;
  onClear: Function;
  valid: boolean;
  timeFormat: boolean | string;
  onFocus: Function;
  onBlur: Function;
  withInput: boolean;
  inputClassName: string;
};

function renderIcon(onClear, value, icon) {
  if (value)
    return (
      <span className="position-absolute form-datepicker-input-icon cursor-pointer" onClick={onClear}>
        <WpIcon name="times" />
      </span>
    );
  if (icon)
    return <span className="position-absolute form-datepicker-input-icon cursor-pointer">{cloneElement(icon)}</span>;
  return (
    <span className="position-absolute form-datepicker-input-icon cursor-pointer">
      <WpIcon name="calendar" />
    </span>
  );
}

export function DateTimePicker(props: Props) {
  const cleanProps = pick(props, [
    'timeConstraints',
    'isValidDate',
    'defaultValue',
    'timeFormat',
    'onFocus',
    'onBlur',
    'className',
    'inputClassName',
  ]);
  const [isOpen, setIsOpen] = useState(false);

  const inputProps = pick(props, ['placeholder', 'disabled']);

  const { onChange, onFocus, onBlur, value, icon, inputClassName, touched, error, dateFormat } = props;

  const handleChange = useCallback(
    data => {
      onChange(data.toISOString());
      setTimeout(onFocus);
      setTimeout(onBlur);
      setIsOpen(false);
    },
    [onBlur, onChange, onFocus]
  );

  const handleClick = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleOutsideClick = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleClearClick = useCallback(() => {
    onChange(null);
    setTimeout(onFocus);
    setTimeout(onBlur);
  }, [onBlur, onChange, onFocus]);

  return (
    <div>
      <div className={`${inputClassName} position-relative form-input`} onClick={handleClick}>
        <InputUi
          className="w-100 h-100 border rounded shadow-none p-2 pr-5"
          value={value ? moment(value).format(dateFormat || 'L') : ''}
          onChange={identity}
          {...inputProps}
        />
        {renderIcon(handleClearClick, value, icon)}
      </div>
      {touched && error ? <p className="form-input-error text-danger">{error}</p> : null}

      {isOpen ? (
        <>
          <div className="position-absolute" style={{ zIndex: 1049 }}>
            <Datetime
              input={false}
              value={value ? moment(value) : null}
              onChange={handleChange}
              viewMode="days"
              locale="en"
              {...cleanProps}
            />
          </div>
          <WpBackdrop onClick={handleOutsideClick} transparent />
        </>
      ) : null}
    </div>
  );
}

export default createFormField(DateTimePicker);
